// index.js - Main file for the web app

import React, { useState, useEffect } from 'react';
import { generateCoverLetter, decrypt } from './services/openai';
import { templates } from './utils/templates';
import { WavyBackground } from './components/ui/wavy-background';

function App() {
  const [view, setView] = useState('');
  const [clientName, setClientName] = useState('');
  const [jobDetails, setJobDetails] = useState('');
  const [portfolio, setPortfolio] = useState([
    { name: '', link: '' },
    { name: '', link: '' },
    { name: '', link: '' },
    { name: '', link: '' },
    { name: '', link: '' }
  ]);
  const [copyNotification, setCopyNotification] = useState('');
  const [expertise, setExpertise] = useState('');
  const [paragraphAmount, setParagraphAmount] = useState('');
  const [name, setName] = useState('');
  const [profession, setProfession] = useState('');
  const [experience, setExperience] = useState('');
  const [industries, setIndustries] = useState('');
  const [skills, setSkills] = useState('');
  const [focus, setFocus] = useState('');
  const [signature, setSignature] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCoverLetter, setGeneratedCoverLetter] = useState('');
  const [error, setError] = useState('');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: view || 'home',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
  }, [view]);

  const generateUpworkCoverLetter = async () => {
    const errors = validateCoverLetter1();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      alert('Please fill in all required fields (including at least one Portfolio Item)');
      return;
    }
    setFieldErrors({});
    const selectedPortfolio = portfolio
      .filter(item => item.name && item.link)
      .map(item => `${item.name}: ${item.link}`)
      .join(', ');

    let template = decrypt(templates.coverLetter);
    
    template = template
      .replace(/{clientName}/g, clientName)
      .replace(/{jobDetails}/g, jobDetails)
      .replace(/{portfolio}/g, selectedPortfolio || 'no specific portfolio selected');

    try {
      setIsGenerating(true);
      setError('');
      const coverLetter = await generateCoverLetter(template);
      setGeneratedCoverLetter(coverLetter);
    } catch (err) {
      setError('Failed to generate cover letter. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateUpworkCoverLetter2 = async () => {
    const errors = validateCoverLetter2();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      alert('Please fill in all required fields (including at least one Portfolio Item)');
      return;
    }
    setFieldErrors({});
    const selectedPortfolio = portfolio
      .filter(item => item.name && item.link)
      .map(item => `${item.name}: ${item.link}`)
      .join(', ');

    let template = decrypt(templates.coverLetter2);
    
    template = template
      .replace(/{expertise}/g, expertise)
      .replace(/{clientName}/g, clientName)
      .replace(/{jobDetails}/g, jobDetails)
      .replace(/{portfolio}/g, selectedPortfolio || 'no specific portfolio selected')
      .replace(/{paragraphs}/g, paragraphAmount);

    try {
      setIsGenerating(true);
      setError('');
      const coverLetter = await generateCoverLetter(template);
      setGeneratedCoverLetter(coverLetter);
    } catch (err) {
      setError('Failed to generate cover letter. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateProfile = async () => {
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      alert('Please fill in all required fields');
      return;
    }
    setFieldErrors({});
    let template = decrypt(templates.profile);
    
    template = template
      .replace(/{fullName}/g, name)
      .replace(/{profession}/g, profession)
      .replace(/{experience}/g, experience)
      .replace(/{industries}/g, industries)
      .replace(/{skills}/g, skills)
      .replace(/{focusAreas}/g, focus)
      .replace(/{signature}/g, signature);

    try {
      setIsGenerating(true);
      setError('');
      const coverLetter = await generateCoverLetter(template);
      setGeneratedCoverLetter(coverLetter);
    } catch (err) {
      setError('Failed to generate profile. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCoverLetter);
      setCopyNotification('Copied!');
      setShowDonationModal(true);
      
      setTimeout(() => {
        setCopyNotification('');
      }, 3000);
    } catch (err) {
      setCopyNotification('Failed to copy');
    }
  };

  const fillExampleInput = () => {
    switch (view) {
      case 'upworkCoverLetter1':
        setClientName('John Doe');
        setJobDetails('Looking for an experienced developer to help build a web application...');
        setPortfolio([
          { name: 'Project 1', link: 'https://example.com/project1' },
          { name: 'Project 2', link: 'https://example.com/project2' },
          { name: 'Project 3', link: 'https://example.com/project3' },
          { name: '', link: '' },
          { name: '', link: '' }
        ]);
        break;

      case 'upworkCoverLetter2':
        setClientName('John Doe');
        setJobDetails('Looking for an experienced developer to help build a web application...');
        setPortfolio([
          { name: 'Project 1', link: 'https://example.com/project1' },
          { name: 'Project 2', link: 'https://example.com/project2' },
          { name: 'Project 3', link: 'https://example.com/project3' },
          { name: '', link: '' },
          { name: '', link: '' }
        ]);
        setExpertise('Web Development');
        setParagraphAmount('3');
        break;

      case 'upworkCoverLetter3':
        setName('John Smith');
        setProfession('Full Stack Developer');
        setExperience('5');
        setIndustries('Technology, E-commerce, Finance');
        setSkills('JavaScript, React, Node.js, TypeScript, MongoDB');
        setFocus('Creating scalable web applications with modern technologies');
        setSignature('Best regards, John Smith');
        break;
      default:
        // Handle default case when view doesn't match any case
        console.log('No matching view found');
        break;
    }
  };

  const switchView = (newView) => {
    // Clear all form fields when switching views
    setClientName('');
    setJobDetails('');
    setPortfolio([
      { name: '', link: '' },
      { name: '', link: '' },
      { name: '', link: '' },
      { name: '', link: '' },
      { name: '', link: '' }
    ]);
    setExpertise('');
    setParagraphAmount('');
    setName('');
    setProfession('');
    setExperience('');
    setIndustries('');
    setSkills('');
    setFocus('');
    setSignature('');
    
    setView(newView);
  };

  const validateCoverLetter1 = () => {
    const errors = {};
    if (!clientName.trim()) errors.clientName = true;
    if (!jobDetails.trim()) errors.jobDetails = true;
    
    // Validasi minimal satu pasang portfolio items
    const hasValidPortfolio = portfolio.some(item => item.name.trim() && item.link.trim());
    if (!hasValidPortfolio) {
      errors['portfolio.0.name'] = true;
      errors['portfolio.0.link'] = true;
    }
    
    return errors;
  };

  const validateCoverLetter2 = () => {
    const errors = {};
    if (!clientName.trim()) errors.clientName = true;
    if (!jobDetails.trim()) errors.jobDetails = true;
    if (!expertise.trim()) errors.expertise = true;
    if (!paragraphAmount.trim()) errors.paragraphAmount = true;
    
    // Validasi minimal satu pasang portfolio items
    const hasValidPortfolio = portfolio.some(item => item.name.trim() && item.link.trim());
    if (!hasValidPortfolio) {
      errors['portfolio.0.name'] = true;
      errors['portfolio.0.link'] = true;
    }
    
    return errors;
  };

  const validateProfile = () => {
    const errors = {};
    if (!name.trim()) errors.name = true;
    if (!profession.trim()) errors.profession = true;
    if (!experience.trim()) errors.experience = true;
    if (!industries.trim()) errors.industries = true;
    if (!skills.trim()) errors.skills = true;
    if (!focus.trim()) errors.focus = true;
    if (!signature.trim()) errors.signature = true;
    return errors;
  };

  const getInputClassName = (fieldName) => {
    const baseClasses = "mt-1 block w-full rounded-md dark:bg-gray-800 dark:text-white shadow-sm focus:ring-primary";
    return `${baseClasses} ${
      fieldErrors[fieldName] 
        ? "border-red-500 dark:border-red-500 focus:border-red-500" 
        : "border-gray-300 dark:border-gray-600 focus:border-primary"
    }`;
  };

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <WavyBackground 
        className="min-h-screen w-full relative"
        colors={['#38bdf8', '#818cf8', '#c084fc']}
        blur={1}
        speed="slow"
        waveWidth={100}
        backgroundFill="#0f172a"
        waveOpacity={0.5}
      >
        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto relative">
            <img 
              src="/web-header-dark.gif"
              alt="Upwork Prompt Generator By Virtual Hug"
              className="mx-auto mb-8 h-24 md:h-32"
            />

            {view === '' && (
              <div className="space-y-4">
                <button
                  onClick={() => switchView('upworkCoverLetter1')}
                  className="w-full glass-effect text-white rounded-lg py-3 px-4 hover:bg-purple-600/50 transition duration-200"
                >
                  1. ✍🏻 Cover Letter (Simple)
                </button>
                <button
                  onClick={() => switchView('upworkCoverLetter2')}
                  className="w-full glass-effect text-white rounded-lg py-3 px-4 hover:bg-purple-600/50 transition duration-200"
                >
                  2. ✍🏻 Cover Letter (Adjustable Expertise & Paragraph)
                </button>
                <button
                  onClick={() => switchView('upworkCoverLetter3')}
                  className="w-full glass-effect text-white rounded-lg py-3 px-4 hover:bg-purple-600/50 transition duration-200 mb-6"
                >
                  3. 🧑‍💼 Profile Generator
                 </button>
                 
                 <div className="h-8"></div>
                 
                 <div className="space-y-4 p-3 bg-blue-50/50 dark:bg-gray-700/50 rounded-md">
                   <p className="text-xs text-gray-400/80 dark:text-gray-300/80 text-center leading-relaxed">
                     If this Upwork AI Generator has been helpful or even life-changing for you, consider supporting its growth with a donation. As a personal passion project, every contribution plays a vital role in ensuring the website's sustainability and continued development. Your generosity truly makes a difference. Thank you! ❤️
                   </p>

                   <div className="space-y-3">
                     <div>
                       <h3 className="text-xs text-gray-400/80 dark:text-gray-300/80 text-center mb-1">Indonesia</h3>
                       <a
                         href="https://trakteer.id/virtualhug/tip"
                         target="_blank"
                         rel="noopener noreferrer"
                         className="w-full bg-red-500/80 text-white/90 py-1.5 px-3 rounded-md hover:bg-red-600/80 transition duration-200 inline-block text-center text-sm"
                       >
                         Donate via Trakteer
                       </a>
                     </div>

                     <div>
                       <h3 className="text-xs text-gray-400/80 dark:text-gray-300/80 text-center mb-1">Worldwide</h3>
                       <a
                         href="https://ko-fi.com/virtualhuggg"
                         target="_blank"
                         rel="noopener noreferrer"
                         className="w-full bg-blue-500/80 text-white/90 py-1.5 px-3 rounded-md hover:bg-blue-600/80 transition duration-200 inline-block text-center text-sm"
                       >
                         Donate via Ko-Fi
                       </a>
                     </div>
                   </div>
                 </div>
               </div>
            )}

            {view === 'upworkCoverLetter1' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => switchView('')}
                    className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </button>

                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <svg 
                        className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <div className="absolute left-0 bottom-8 w-64 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible">
                        <div className="text-white font-medium mb-2">Tips:</div>
                        <p>The Example Input button is your quick guide to grasp the purpose of each input field below.</p>
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                      </div>
                    </div>
                    <button
                      onClick={fillExampleInput}
                      className="text-white hover:text-white flex items-center gap-2 bg-blue-700 hover:bg-blue-800 px-3 py-1 rounded-md transition-colors"
                    >
                      Example Input
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Client Name
                      </label>
                      <div className="relative group">
                        <svg 
                          className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute left-0 top-full transform translate-y-2 w-80 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50">
                          <p className="font-semibold mb-2">Tips:</p>
                          <ul className="list-disc pl-4 space-y-2">
                            <li>Addressing potential clients by their name can make your cover letter feel more personal and appealing.</li>                        
                            <li>If the client's name isn't mentioned in the "Job Details" section on Upwork, try scrolling down to the "Client's Recent History" section. In this section, clients often receive stars and feedback, where freelancers typically refer to them by their actual name.</li>
                            <li>If you're still unable to find the client's name, you can leave the "Client Name" field as "Anonymous".</li>
                          </ul>
                          <div className="absolute -top-2 left-4 transform w-3 h-3 bg-black rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g., John Wick"
                      className={getInputClassName('clientName')}
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Job Details
                      </label>
                      <div className="relative group">
                        <svg 
                          className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute left-0 top-full transform translate-y-2 w-80 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50">
                          <p className="font-semibold mb-2">Tips:</p>
                          <ul className="list-disc pl-4 space-y-2">
                            <li>Select the job details offered by the prospective client that match your expertise, and paste it here.</li>
                          </ul>
                          <div className="absolute -top-2 left-4 transform w-3 h-3 bg-black rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <textarea
                      value={jobDetails}
                      onChange={(e) => setJobDetails(e.target.value)}
                      placeholder="Paste unedited job description from Upwork client here..."
                      rows={10}
                      className={getInputClassName('jobDetails')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Portfolio Items (max 5)
                    </label>
                    <div className="space-y-4">
                      {portfolio.map((item, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => {
                                const newPortfolio = [...portfolio];
                                newPortfolio[index].name = e.target.value;
                                setPortfolio(newPortfolio);
                              }}
                              placeholder="Project name"
                              className={getInputClassName(`portfolio.${index}.name`)}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.link}
                              onChange={(e) => {
                                const newPortfolio = [...portfolio];
                                newPortfolio[index].link = e.target.value;
                                setPortfolio(newPortfolio);
                              }}
                              placeholder="Project link"
                              className={getInputClassName(`portfolio.${index}.link`)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <button
                  onClick={generateUpworkCoverLetter}
                  disabled={isGenerating}
                  className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg py-3 px-4 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <span>Generate with AI</span>
                  )}
                </button>
              </div>
            )}

            {view === 'upworkCoverLetter2' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => switchView('')}
                    className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </button>

                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <svg 
                        className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <div className="absolute left-0 bottom-8 w-64 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible">
                        <div className="text-white font-medium mb-2">Tips:</div>
                        <p>The Example Input button is your quick guide to grasp the purpose of each input field below.</p>
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                      </div>
                    </div>
                    <button
                      onClick={fillExampleInput}
                      className="text-white hover:text-white flex items-center gap-2 bg-blue-700 hover:bg-blue-800 px-3 py-1 rounded-md transition-colors"
                    >
                      Example Input
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Client Name
                      </label>
                      <div className="relative group">
                        <svg 
                          className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute left-0 top-full transform translate-y-2 w-80 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50">
                          <p className="font-semibold mb-2">Tips:</p>
                          <ul className="list-disc pl-4 space-y-2">
                            <li>Addressing potential clients by their name can make your cover letter feel more personal and appealing.</li>                        
                            <li>If the client's name isn't mentioned in the "Job Details" section on Upwork, try scrolling down to the "Client's Recent History" section. In this section, clients often receive stars and feedback, where freelancers typically refer to them by their actual name.</li>
                            <li>If you're still unable to find the client's name, you can leave the "Client Name" field as "Anonymous".</li>
                          </ul>
                          <div className="absolute -top-2 left-4 transform w-3 h-3 bg-black rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g., John Wick"
                      className={getInputClassName('clientName')}
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Job Details
                      </label>
                      <div className="relative group">
                        <svg 
                          className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute left-0 top-full transform translate-y-2 w-80 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50">
                          <p className="font-semibold mb-2">Tips:</p>
                          <ul className="list-disc pl-4 space-y-2">
                            <li>Select the job details offered by the prospective client that match your expertise, and paste it here.</li>
                          </ul>
                          <div className="absolute -top-2 left-4 transform w-3 h-3 bg-black rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <textarea
                      value={jobDetails}
                      onChange={(e) => setJobDetails(e.target.value)}
                      placeholder="Paste unedited job description from Upwork client here..."
                      rows={10}
                      className={getInputClassName('jobDetails')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your Expertise
                    </label>
                    <input
                      type="text"
                      value={expertise}
                      onChange={(e) => setExpertise(e.target.value)}
                      placeholder="e.g., Web Development, UI/UX Design"
                      className={getInputClassName('expertise')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Portfolio Items (max 5)
                    </label>
                    <div className="space-y-4">
                      {portfolio.map((item, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => {
                                const newPortfolio = [...portfolio];
                                newPortfolio[index].name = e.target.value;
                                setPortfolio(newPortfolio);
                              }}
                              placeholder="Project name"
                              className={getInputClassName(`portfolio.${index}.name`)}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.link}
                              onChange={(e) => {
                                const newPortfolio = [...portfolio];
                                newPortfolio[index].link = e.target.value;
                                setPortfolio(newPortfolio);
                              }}
                              placeholder="Project link"
                              className={getInputClassName(`portfolio.${index}.link`)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Desired number of paragraphs
                    </label>
                    <input
                      type="number"
                      value={paragraphAmount}
                      onChange={(e) => setParagraphAmount(e.target.value)}
                      min="1"
                      max="10"
                      placeholder="e.g., 3"
                      className={getInputClassName('paragraphAmount')}
                    />
                  </div>
                </div>

                <button
                  onClick={generateUpworkCoverLetter2}
                  disabled={isGenerating}
                  className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg py-3 px-4 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <span>Generate with AI</span>
                  )}
                </button>
              </div>
            )}

            {view === 'upworkCoverLetter3' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => switchView('')}
                    className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </button>

                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <svg 
                        className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                        />
                      </svg>
                      <div className="absolute left-0 bottom-8 w-64 bg-black text-white text-sm rounded-lg p-4 invisible group-hover:visible">
                        <div className="text-white font-medium mb-2">Tips:</div>
                        <p>The Example Input button is your quick guide to grasp the purpose of each input field below.</p>
                        <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                      </div>
                    </div>
                    <button
                      onClick={fillExampleInput}
                      className="text-white hover:text-white flex items-center gap-2 bg-blue-700 hover:bg-blue-800 px-3 py-1 rounded-md transition-colors"
                    >
                      Example Input
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., John Wick"
                      className={getInputClassName('name')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your Profession/Role
                    </label>
                    <input
                      type="text"
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      placeholder="e.g., Video Editor, Content Creator"
                      className={getInputClassName('profession')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Years of Experience
                    </label>
                    <input
                      type="number"
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      min="0"
                      className={getInputClassName('experience')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Industries You've Worked In (separated by commas)
                    </label>
                    <input
                      type="text"
                      value={industries}
                      onChange={(e) => setIndustries(e.target.value)}
                      className={getInputClassName('industries')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Key Skills (separated by commas)
                    </label>
                    <textarea
                      value={skills}
                      onChange={(e) => setSkills(e.target.value)}
                      placeholder="e.g., Video Editing, Social Media Posts"
                      rows="4"
                      className={getInputClassName('skills')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your Focus/Offerings
                    </label>
                    <textarea
                      value={focus}
                      onChange={(e) => setFocus(e.target.value)}
                      placeholder="e.g., delivering high-quality content"
                      rows="4"
                      className={getInputClassName('focus')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Closing Signature
                    </label>
                    <input
                      type="text"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="e.g., Best regards, John Wick"
                      className={getInputClassName('signature')}
                      required
                    />
                  </div>
                </div>

                <button
                  onClick={generateProfile}
                  disabled={isGenerating}
                  className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg py-3 px-4 transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <span>Generate with AI</span>
                  )}
                </button>
              </div>
            )}

            {generatedCoverLetter && (
              <div className="mt-8 bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">
                  {view === 'upworkCoverLetter3' ? 'Generated Profile:' : 'Generated Cover Letter:'}
                </h2>
                <div className="bg-gray-900/50 p-4 rounded-md mb-4">
                  <p className="text-gray-300 whitespace-pre-wrap">
                    {generatedCoverLetter}
                  </p>
                </div>
                
                <div className="flex flex-col items-start gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-secondary transition duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" 
                      />
                    </svg>
                    Copy to Clipboard
                  </button>
                  
                  {copyNotification && (
                    <div className="text-sm text-green-400">
                      {copyNotification}
                    </div>
                  )}

                  {showDonationModal && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-gray-700 rounded-lg text-sm">
                      <p className="text-gray-700 dark:text-gray-300 mb-4">
                      If this Upwork AI Generator has been helpful or even life-changing for you, consider supporting its growth with a donation. As a personal passion project, every contribution plays a vital role in ensuring the website’s sustainability and continued development. Your generosity truly makes a difference. Thank you! ❤️
                      </p>
                      <div className="space-y-2">
                        <p className="font-medium text-gray-900 dark:text-white">Indonesia</p>
                        <a 
                          href="https://trakteer.id/virtualhug/tip"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline block"
                        >
                          https://trakteer.id/virtualhug/tip
                        </a>
                        
                        <p className="font-medium text-gray-900 dark:text-white mt-3">Worldwide</p>
                        <a 
                          href="https://ko-fi.com/virtualhuggg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline block"
                        >
                          https://ko-fi.com/virtualhuggg
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Donation Modal */}
            {showDonationModal && (
              <div className="fixed inset-0 bg-gray-900/80 flex items-center justify-center z-50">
                <div className="glass-effect-strong rounded-lg p-8 max-w-lg w-full mx-4 relative">
                  {/* Notifikasi copied */}
                  <div className="flex items-center justify-center text-green-500 mb-4">
                    <svg 
                      className="w-5 h-5 mr-2" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="font-medium">Copied!</span>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => setShowDonationModal(false)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Modal content */}
                  <div className="space-y-6">
                    <p className="text-gray-300 text-center">
                    If this Upwork AI Generator has been helpful or even life-changing for you, consider supporting its growth with a donation. As a personal passion project, every contribution plays a vital role in ensuring the website’s sustainability and continued development. Your generosity truly makes a difference. Thank you! ❤️
                    </p>

                    {/* Tambahkan GIF di sini */}
                    <div className="flex justify-center">
                      <iframe 
                        src="https://giphy.com/embed/NB0T7lZE7p6zQsN6f9" 
                        title="GitHub Sponsors"
                        width="200" 
                        height="200" 
                        frameBorder="0" 
                        allowFullScreen 
                        className="rounded-lg"
                      ></iframe>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium text-white mb-2">Indonesia</h3>
                        <a 
                          href="https://trakteer.id/virtualhug/tip"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition duration-200 inline-block text-center"
                        >
                          Donate via Trakteer
                        </a>
                      </div>

                      <div>
                        <h3 className="font-medium text-white mb-2">Worldwide</h3>
                        <a 
                          href="https://ko-fi.com/virtualhuggg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 inline-block text-center"
                        >
                          Donate via Ko-Fi
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </WavyBackground>
    </div>
  );
}

export default App;

